import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { PhoneInput } from '@/components/ui/PhoneInput/PhoneInput';
import { SingleDatePicker } from '@/components/ui';
import { LocationAutocomplete } from '@/components/ui/LocationAutocomplete/LocationAutocomplete';
import { Input } from '@/components/ui/Input/Input';
import { getCountryFromPhone } from '@/utils/phoneUtils';
import { PublicToggle } from '@/pages/Organizations/components/shared/PublicToggle';
import type { SettingsFormReturn } from '../useSettingsForm';

const MAX_ABOUT_WORDS = 100;

function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

/** Caps `text` at `max` words, keeping the first `max` words when exceeded. */
function limitToWords(text: string, max: number): string {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= max) return text;
  return words.slice(0, max).join(' ');
}

export function AccountInfoCard({ form }: { form: SettingsFormReturn }) {
  const {
    isVolunteer,
    firstName, setFirstName,
    lastName, setLastName,
    email,
    phone, setPhone,
    phoneCountryCode, setPhoneCountryCode,
    phoneError,
    description, setDescription,
    dateOfBirth, setDateOfBirth,
    tshirtSize, setTshirtSize,
    name, setName,
    orgEmail,
    orgPhone, setOrgPhone,
    orgPhoneCountryCode, setOrgPhoneCountryCode,
    orgPhoneError,
    orgDescription, setOrgDescription,
    location, setLocation,
    marketplaceListed, setMarketplaceListed,
    handlePhoneValidation,
    handleOrgPhoneValidation,
  } = form;
  return (
    <Box
      bg="white"
      borderRadius="2xl"
      p="6"
      boxShadow="0 8px 24px rgba(0,0,0,0.12)"
      minH={{ lg: '320px' }}
    >
      <Text as="h3" fontSize="lg" fontWeight="semibold" color="volyx.primary" mb="4">
        Account Info
      </Text>

      {isVolunteer ? (
        <>
          <chakra.label htmlFor="first-name" display="block" mb="2" fontSize="sm" fontWeight="medium" color="volyx.text" mt="0">
            First Name
          </chakra.label>
          <Input
            id="first-name"
            name="first-name"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={20}
          />

          <chakra.label htmlFor="last-name" display="block" mb="2" fontSize="sm" fontWeight="medium" color="volyx.text" mt="4">
            Last Name
          </chakra.label>
          <Input
            id="last-name"
            name="last-name"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            maxLength={20}
          />

          <chakra.label htmlFor="description" display="block" mb="2" fontSize="sm" fontWeight="medium" color="volyx.text" mt="4">
            Personal Description
          </chakra.label>
          <chakra.textarea
            id="description"
            name="description"
            autoComplete="off"
            w="full"
            minH="160px"
            bg="volyx.lightGray"
            borderRadius="xl"
            border="none"
            px="4"
            py="3"
            fontSize="md"
            color="volyx.text"
            boxShadow="0 1px 3px rgba(0,0,0,0.08)"
            transition="all 0.2s"
            resize="none"
            _focus={{ outline: 'none', boxShadow: '0 0 0 3px rgba(31,41,50,0.1)', transform: 'scale(1.01)' }}
            _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
            value={description}
            onChange={(e) => setDescription(limitToWords(e.target.value, MAX_ABOUT_WORDS))}
            rows={5}
          />
          <Text fontSize="sm" color="volyx.textSecondary" mt="1" textAlign="right">
            {countWords(description)}/{MAX_ABOUT_WORDS} words
          </Text>

          <chakra.label htmlFor="email" display="block" mb="2" fontSize="sm" fontWeight="medium" color="volyx.text" mt="4">
            Email
          </chakra.label>
          <Input id="email" name="email" autoComplete="email" value={email} disabled />

          <chakra.label htmlFor="phone" display="block" mb="2" fontSize="sm" fontWeight="medium" color="volyx.text" mt="4">
            Phone
          </chakra.label>
          <PhoneInput
            id="phone"
            name="phone"
            autoComplete="tel"
            value={phone}
            onChange={(value) => {
              setPhone(value);
              if (value && value.startsWith('+')) {
                const countryCode = getCountryFromPhone(value);
                if (countryCode) setPhoneCountryCode(countryCode.toUpperCase());
              }
            }}
            onCountryChange={setPhoneCountryCode}
            onValidationChange={handlePhoneValidation}
            defaultCountry={phoneCountryCode}
            error={phoneError}
          />
          {phoneError && (
            <Text fontSize="sm" color="volyx.error" mt="1">{phoneError}</Text>
          )}

          <chakra.label htmlFor="date-of-birth" display="block" mb="2" fontSize="sm" fontWeight="medium" color="volyx.text" mt="4">
            Date of Birth
          </chakra.label>
          <SingleDatePicker
            popover
            id="date-of-birth"
            name="date-of-birth"
            autoComplete="off"
            value={dateOfBirth}
            onChange={(date) => setDateOfBirth(date ?? '')}
            maxDate={new Date()}
            showAge
          />

          <Box as="fieldset" border="none" p="0" mt="4">
            <Box as="legend" display="block" mb="2" fontSize="sm" fontWeight="medium" color="volyx.text">
              T-Shirt Size
            </Box>
            <Flex gap="2" flexWrap="wrap">
              {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map((size) => (
                <chakra.button
                  key={size}
                  type="button"
                  px="6"
                  py="2"
                  border="0"
                  borderRadius="xl"
                  fontSize="md"
                  fontWeight="medium"
                  cursor="pointer"
                  transition="color 0.15s, background-color 0.15s"
                  outline="none"
                  _focus={{ outline: 'none' }}
                  bg={size === tshirtSize ? 'volyx.action' : 'volyx.lightGray'}
                  color={size === tshirtSize ? 'white' : 'volyx.text'}
                  _hover={size !== tshirtSize ? { bg: 'volyx.mediumGray' } : undefined}
                  onClick={() => setTshirtSize(size)}
                >
                  {size}
                </chakra.button>
              ))}
            </Flex>
          </Box>
        </>
      ) : (
        <>
          <Box as="label" display="block" mb="2" fontSize="sm" fontWeight="medium" color="volyx.text">
            Organization Name
          </Box>
          <Input
            id="org-name"
            name="org-name"
            autoComplete="organization"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <Box as="label" display="block" mb="2" fontSize="sm" fontWeight="medium" color="volyx.text" mt="4">
            Email
          </Box>
          <Input id="org-email" name="org-email" autoComplete="email" value={orgEmail} disabled />

          <Box as="label" display="block" mb="2" fontSize="sm" fontWeight="medium" color="volyx.text" mt="4">
            Phone
          </Box>
          <PhoneInput
            id="org-phone"
            name="org-phone"
            autoComplete="tel"
            value={orgPhone}
            onChange={(value) => {
              setOrgPhone(value);
              if (value && value.startsWith('+')) {
                const countryCode = getCountryFromPhone(value);
                if (countryCode) setOrgPhoneCountryCode(countryCode.toUpperCase());
              }
            }}
            onCountryChange={setOrgPhoneCountryCode}
            onValidationChange={handleOrgPhoneValidation}
            defaultCountry={orgPhoneCountryCode}
            error={orgPhoneError}
          />
          {orgPhoneError && (
            <Text fontSize="sm" color="volyx.error" mt="1">{orgPhoneError}</Text>
          )}

          <chakra.label htmlFor="org-description" display="block" mb="2" fontSize="sm" fontWeight="medium" color="volyx.text" mt="4">
            Description
          </chakra.label>
          <chakra.textarea
            id="org-description"
            name="org-description"
            autoComplete="off"
            w="full"
            minH="160px"
            bg="volyx.lightGray"
            borderRadius="xl"
            border="none"
            px="4"
            py="3"
            fontSize="md"
            color="volyx.text"
            boxShadow="0 1px 3px rgba(0,0,0,0.08)"
            transition="all 0.2s"
            resize="none"
            _focus={{ outline: 'none', boxShadow: '0 0 0 3px rgba(31,41,50,0.1)', transform: 'scale(1.01)' }}
            _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
            value={orgDescription}
            onChange={(e) => setOrgDescription(limitToWords(e.target.value, MAX_ABOUT_WORDS))}
            rows={5}
          />
          <Text fontSize="sm" color="volyx.textSecondary" mt="1" textAlign="right">
            {countWords(orgDescription)}/{MAX_ABOUT_WORDS} words
          </Text>

          <chakra.label htmlFor="org-location" display="block" mb="2" fontSize="sm" fontWeight="medium" color="volyx.text" mt="4">
            Location
          </chakra.label>
          <LocationAutocomplete
            id="org-location"
            name="org-location"
            value={location}
            onChange={setLocation}
            placeholder="Search for a city..."
          />

          <Box mt="6">
            <PublicToggle
              isPublic={marketplaceListed}
              onToggle={setMarketplaceListed}
              title="Marketplace listing"
              publicLabel="Public"
              privateLabel="Private"
              helperWhenPublic="Your organization appears in the volunteer marketplace for discovery."
              helperWhenPrivate="Only accepted members and volunteers with a pending request can see your organization in the marketplace."
            />
          </Box>
        </>
      )}
    </Box>
  );
}
